import React from 'react';
import {
  Clock,
  Calendar,
  Edit3,
  ArrowRight,
  User,
  Tag,
  Hash,
  ImageOff
} from 'lucide-react';
import { Link } from 'react-router-dom';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return null;
  }
}

const STATUS_CONFIG = {
  published: {
    label: 'Published',
    dot: 'bg-emerald-400',
    badge: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20'
  },
  draft: {
    label: 'Draft',
    dot: 'bg-zinc-400',
    badge: 'text-zinc-400 bg-zinc-400/10 border-zinc-400/20'
  },
  archived: {
    label: 'Archived',
    dot: 'bg-rose-400',
    badge: 'text-rose-400 bg-rose-400/10 border-rose-400/20'
  }
};
const BlogPostCard = ({ post = {} }) => {
  const {
    author,
    categories = [],
    created_at,
    excerpt,
    reading_time,
    status = 'draft',
    tags = [],
    title = 'Untitled',
    featured_image,
    publish_date
  } = post;

  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const displayDate = formatDate(publish_date || created_at);

  return (
    <article className="flex flex-col border border-black/[0.07] rounded-2xl overflow-hidden cursor-pointer hover:scale-[1.02] duration-300 transition-all ease-in-out">
      <div className="relative h-48 overflow-hidden bg-[#1E1E1E] shrink-0">
        {featured_image ? (
          <img src={featured_image} alt={title} />
        ) : (
          <div className="flex justify-center items-center w-full h-full">
            <ImageOff size={28} className="text-white/10" />
          </div>
        )}

        {featured_image && (
          <div className="absolute inset-0 bg-gradient-to-t from-[#161616]/60 via-transparent to-transparent" />
        )}

        {/* Status badge */}
        <div className="absolute top-3 left-3">
          <span
            className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold rounded-full border backdrop-blur-sm ${statusCfg.badge}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
            {statusCfg.label}
          </span>
        </div>

        {/* Edit button */}
        <Link
          to={`/edit-story/${post?._id}`}
          className="flex absolute top-3 right-3 justify-center items-center w-8 h-8 rounded-full border backdrop-blur-sm transition-all bg-black/40 hover:bg-black/70 border-white/10 text-white/60 hover:text-white"
          aria-label="Edit post"
        >
          <Edit3 size={13} />
        </Link>
      </div>
      <div className="flex flex-col flex-1 p-5">
        {/* Categories */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {categories.slice(0, 3).map((cat, i) => (
              <span
                key={i}
                className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-amber-400/80 bg-amber-400/[0.08] border border-amber-400/15 rounded-full"
                onClick={(e) => e.stopPropagation()}
              >
                <Hash size={9} />
                {cat}
              </span>
            ))}
            {categories.length > 3 && (
              <span className="text-[10px] text-white/20 self-center">
                +{categories.length - 3}
              </span>
            )}
          </div>
        )}

        <h2 className="mb-2.5 text-xl font-light leading-snug text-black/90 line-clamp-2">
          {title}
        </h2>

        {/* Excerpt */}
        {excerpt && (
          <p className="flex-1 mb-4 text-sm leading-relaxed text-black/40 line-clamp-3">
            {excerpt}
          </p>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {tags.slice(0, 4).map((tag, i) => (
              <span
                key={i}
                className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-white/30 bg-black/[0.04] border border-white/[0.07] rounded-full"
                onClick={(e) => e.stopPropagation()}
              >
                <Tag size={8} />
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-black/[0.06]">
          <div className="flex items-center gap-3 text-black/25 text-[11px]">
            {author && (
              <span className="flex gap-1 items-center">
                <User size={11} />
                {author}
              </span>
            )}
            {displayDate && (
              <span className="flex gap-1 items-center">
                <Calendar size={11} />
                {displayDate}
              </span>
            )}

            <span className="flex gap-1 items-center">
              <Clock size={11} />
              {reading_time}m read
            </span>
          </div>

          <Link
            to={`/read-story/${post?._id}`}
            className="flex items-center gap-1 text-amber-400 text-[11px] font-semibold hover:text-amber-300 transition-colors"
          >
            Read <ArrowRight size={10} />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default BlogPostCard;
